import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, X } from 'lucide-react';

const emptyItem = { sku: '', name: '', quantity: 0, unit_price: 0, vat_rate: 0, rejection_reason: '' };

const DebitNotes = () => {
  const [debitNotes, setDebitNotes] = useState([]);
  const [qcs, setQcs] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currencyRate, setCurrencyRate] = useState(1);
  const [formData, setFormData] = useState({ qc_id: '', currency: 'UGX', notes: '', items: [{ ...emptyItem }] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [dnRes, qcRes, poRes, curRes] = await Promise.all([
        api.get('/purchase/debit-notes'),
        api.get('/purchase/qc'),
        api.get('/purchase/orders'),
        api.get('/config/currency').catch(() => ({ data: { usd_to_ugx_rate: 1 } })),
      ]);
      setDebitNotes(dnRes.data);
      setQcs(qcRes.data);
      setPos(poRes.data);
      setCurrencyRate(curRes.data?.usd_to_ugx_rate || 1);
    } catch (error) {
      console.error('Failed to fetch debit notes:', error);
    } finally { setLoading(false); }
  };

  // QC inspections that have at least one rejected item
  const rejectedQcs = qcs.filter(qc => (qc.items || []).some(it => (it.rejected_quantity || 0) > 0));

  const openCreate = () => {
    setFormData({ qc_id: '', currency: 'UGX', notes: '', items: [{ ...emptyItem }] });
    setShowModal(true);
  };

  const onSelectQc = (qcId) => {
    const qc = qcs.find(q => q.id === qcId);
    if (!qc) { setFormData({ ...formData, qc_id: qcId }); return; }
    const po = pos.find(p => p.id === qc.po_id);
    const items = (qc.items || []).filter(it => (it.rejected_quantity || 0) > 0).map(it => {
      const poItem = (po?.items || []).find(pi => pi.sku === it.sku);
      return {
        sku: it.sku, name: it.name,
        quantity: it.rejected_quantity || 0,
        unit_price: poItem?.unit_price || 0,
        vat_rate: poItem?.vat_rate || 0,
        rejection_reason: it.rejection_reason || qc.remarks || 'Rejected in QC',
      };
    });
    setFormData({
      ...formData,
      qc_id: qcId,
      currency: po?.currency || 'UGX',
      items: items.length ? items : [{ ...emptyItem }],
    });
  };

  const updateItem = (idx, field, value) => {
    const items = [...formData.items];
    items[idx][field] = value;
    setFormData({ ...formData, items });
  };
  const addItem = () => setFormData({ ...formData, items: [...formData.items, { ...emptyItem }] });
  const removeItem = (idx) => { if (formData.items.length > 1) setFormData({ ...formData, items: formData.items.filter((_, i) => i !== idx) }); };

  const changeCurrency = (newCurrency) => {
    const rate = currencyRate || 1;
    let items = formData.items;
    if (newCurrency !== formData.currency && rate && rate !== 1) {
      const factor = newCurrency === 'USD' ? (1 / rate) : rate;
      items = formData.items.map(it => ({ ...it, unit_price: Math.round((it.unit_price || 0) * factor * 100) / 100 }));
    }
    setFormData({ ...formData, currency: newCurrency, items });
  };

  const lineTotals = (item) => {
    const taxable = (item.quantity || 0) * (item.unit_price || 0);
    const vat_amount = (taxable * (item.vat_rate || 0)) / 100;
    return { ...item, taxable_amount: taxable, vat_amount, total_amount: taxable + vat_amount };
  };

  const totals = formData.items.reduce((acc, it) => {
    const c = lineTotals(it);
    acc.subtotal += c.taxable_amount; acc.vat += c.vat_amount; acc.total += c.total_amount;
    return acc;
  }, { subtotal: 0, vat: 0, total: 0 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.qc_id) { alert('Select a QC inspection'); return; }
    const items = formData.items.filter(i => i.sku && i.quantity > 0).map(lineTotals);
    if (!items.length) { alert('Add at least one rejected item'); return; }
    const exchange_rate = formData.currency === 'USD' ? (currencyRate || 1) : 1;
    try {
      await api.post('/purchase/debit-notes', { qc_id: formData.qc_id, items, currency: formData.currency, exchange_rate, notes: formData.notes });
      setShowModal(false);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create debit note'); }
  };

  return (
    <div data-testid="debit-notes-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Debit Notes</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-dn-btn"><Plus size={16} /> Create Debit Note</button>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading debit notes...</div>
        ) : debitNotes.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No debit notes yet. Create one from a QC inspection that has rejected materials.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>DN #</th>
                  <th>Vendor</th>
                  <th>VAT No</th>
                  <th>Items</th>
                  <th>Currency</th>
                  <th className="text-right">Subtotal</th>
                  <th className="text-right">VAT</th>
                  <th className="text-right">Total</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {debitNotes.map((dn) => (
                  <tr key={dn.id} data-testid={`dn-row-${dn.debit_note_number}`}>
                    <td className="font-mono">{dn.debit_note_number}</td>
                    <td>{dn.vendor_name}</td>
                    <td className="font-mono text-sm">{dn.vendor_gstin || '-'}</td>
                    <td>{dn.items?.length || 0} items</td>
                    <td><span className="badge badge-info">{dn.currency || 'UGX'}</span></td>
                    <td className="text-right tabular-nums">{(dn.subtotal || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="text-right tabular-nums">{(dn.vat_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="text-right tabular-nums font-semibold">{dn.currency || 'UGX'} {(dn.total_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="text-sm">{new Date(dn.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="dn-modal">
          <div className="modal-content" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create Debit Note</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Currency</span>
                <select className="form-select" style={{ width: 'auto', fontSize: '13px', padding: '4px 10px' }} value={formData.currency} onChange={(e) => changeCurrency(e.target.value)} data-testid="currency-select">
                  <option value="UGX">UGX (base)</option>
                  <option value="USD">USD (1 USD = {currencyRate} UGX)</option>
                </select>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">QC Inspection (with rejected items) *</label>
                  <select className="form-select" value={formData.qc_id} onChange={(e) => onSelectQc(e.target.value)} required data-testid="qc-select">
                    <option value="">Select QC inspection...</option>
                    {rejectedQcs.map(qc => {
                      const po = pos.find(p => p.id === qc.po_id);
                      const rej = (qc.items || []).reduce((s, it) => s + (it.rejected_quantity || 0), 0);
                      return <option key={qc.id} value={qc.id}>{qc.qc_number} — {po?.po_number || 'PO'} ({rej} rejected)</option>;
                    })}
                  </select>
                  {rejectedQcs.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>No QC inspections with rejected items found.</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Rejected Items</label>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px' }}>SKU</th>
                          <th style={{ padding: '4px' }}>Name</th>
                          <th style={{ padding: '4px' }}>Qty</th>
                          <th style={{ padding: '4px' }}>Price</th>
                          <th style={{ padding: '4px' }}>VAT%</th>
                          <th style={{ padding: '4px' }}>Rejection Reason</th>
                          <th style={{ padding: '4px', textAlign: 'right' }}>Amount</th>
                          <th style={{ padding: '4px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => {
                          const t = lineTotals(item);
                          return (
                            <tr key={index}>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.sku} onChange={(e) => updateItem(index, 'sku', e.target.value)} style={{ width: '90px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.name} onChange={(e) => updateItem(index, 'name', e.target.value)} style={{ minWidth: '120px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)} style={{ width: '60px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)} style={{ width: '80px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.vat_rate} onChange={(e) => updateItem(index, 'vat_rate', parseFloat(e.target.value) || 0)} style={{ width: '50px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.rejection_reason} onChange={(e) => updateItem(index, 'rejection_reason', e.target.value)} style={{ minWidth: '140px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px', textAlign: 'right' }} className="tabular-nums">{t.total_amount.toFixed(2)}</td>
                              <td style={{ padding: '4px' }}>{formData.items.length > 1 && <button type="button" onClick={() => removeItem(index)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} data-testid="add-item-btn">+ Add Item</button>
                </div>

                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px', textAlign: 'right', fontSize: '13px' }}>
                  <div>Subtotal: <span className="tabular-nums" style={{ marginLeft: '8px' }}>{totals.subtotal.toFixed(2)}</span></div>
                  <div>VAT: <span className="tabular-nums" style={{ marginLeft: '8px' }}>{totals.vat.toFixed(2)}</span></div>
                  <div style={{ fontWeight: 700, fontSize: '15px', marginTop: '4px' }}>Total ({formData.currency}): <span className="tabular-nums" style={{ marginLeft: '8px' }}>{totals.total.toFixed(2)}</span></div>
                  {formData.currency === 'USD' && <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>≈ UGX {(totals.total * (currencyRate || 1)).toLocaleString()} (@ {currencyRate})</div>}
                </div>

                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-dn-btn">Create Debit Note</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebitNotes;
